/* Lease maths. Pure functions over numbers — no React, no data imports — so
   the inventory grid, the compact calculator and the full calculator page all
   quote the same instalment for the same car.

   Every function here is total: given any input, including nonsense a text
   field can produce, it returns a finite number. Nothing downstream should
   ever have to guard against NaN. */

/** Terms the headline instalment assumes; surfaced in the UI, never hidden. */
export const DOWN_PAYMENT_RATE = 0.3
export const MARKUP_APR = 0.16
export const TENURE_YEARS = 5

export const MIN_TENURE_YEARS = 1
export const MAX_TENURE_YEARS = 7

const finite = (value: number, fallback = 0) =>
  Number.isFinite(value) ? value : fallback

export const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(finite(value, min), min), max)

/**
 * Standard annuity instalment.
 *
 *   monthly = P·r / (1 − (1 + r)^−n)
 *
 * where P is the financed amount, r the monthly markup rate and n the number
 * of months. At zero markup the formula divides by zero, so that case repays
 * the principal in equal parts instead.
 */
export function monthlyPayment(
  principal: number,
  annualRate: number,
  months: number,
) {
  const p = finite(principal)
  const n = Math.round(finite(months))

  if (p <= 0 || n <= 0) return 0

  const r = finite(annualRate) / 12
  if (r <= 0) return Math.round(p / n)

  return Math.round((p * r) / (1 - (1 + r) ** -n))
}

export type QuoteInput = {
  price: number
  /** Absolute rupees, not a rate. Clamped into [0, price]. */
  downPayment: number
  years: number
  /** Annual markup as a rate: 0.1449 is 14.49%. */
  markupRate: number
}

export type Quote = {
  price: number
  downPayment: number
  downRate: number
  financed: number
  months: number
  markupRate: number
  monthly: number
  /** Everything the customer hands over: instalments plus the down payment. */
  totalPayable: number
  /** What the financing costs on top of the financed amount. */
  totalMarkup: number
}

export function quote({
  price,
  downPayment,
  years,
  markupRate,
}: QuoteInput): Quote {
  const safePrice = Math.max(finite(price), 0)
  /* A down payment above the price is not a bigger deposit, it is a typo. */
  const safeDown = clamp(downPayment, 0, safePrice)
  const safeYears = clamp(years, 0, MAX_TENURE_YEARS)
  const safeRate = Math.max(finite(markupRate), 0)

  const financed = safePrice - safeDown
  const months = Math.round(safeYears * 12)
  const monthly = monthlyPayment(financed, safeRate, months)
  const instalmentTotal = monthly * months

  return {
    price: safePrice,
    downPayment: safeDown,
    downRate: safePrice > 0 ? safeDown / safePrice : 0,
    financed,
    months,
    markupRate: safeRate,
    monthly,
    totalPayable: instalmentTotal + safeDown,
    totalMarkup: Math.max(instalmentTotal - financed, 0),
  }
}

/** The headline figure on a vehicle card, on the house's standard terms. */
export function instalmentFor(
  price: number,
  downRate = DOWN_PAYMENT_RATE,
  apr = MARKUP_APR,
  years = TENURE_YEARS,
) {
  return quote({
    price,
    downPayment: price * downRate,
    years,
    markupRate: apr,
  }).monthly
}
